import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useSessao } from "@/hooks/use-sessao";
import { ApiError, apiFetch } from "@/lib/api";

const schema = z.object({
  email: z.string().email("E-mail inválido"),
  senha: z.string().min(6, "Senha tem no mínimo 6 caracteres"),
});

type Form = z.infer<typeof schema>;

export function LoginPage() {
  const { autenticado, ehAdmin, carregando } = useSessao();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const form = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", senha: "" },
  });

  if (!carregando && autenticado && ehAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  async function enviar(data: Form) {
    try {
      await apiFetch("/api/auth/sign-in", { method: "POST", body: data });
      await qc.invalidateQueries({ queryKey: ["sessao"] });
      toast.success("Bem-vindo(a)");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "E-mail ou senha inválidos";
      toast.error(msg);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Painel administrativo</CardTitle>
          <CardDescription>Acesso restrito a administradores. Entre com seu e-mail e senha.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={form.handleSubmit(enviar)} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="email">E-mail</Label>
              <Input id="email" type="email" autoComplete="email" maxLength={254} {...form.register("email")} />
              {form.formState.errors.email && (
                <p className="text-xs text-destructive">{form.formState.errors.email.message}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="senha">Senha</Label>
              <Input id="senha" type="password" autoComplete="current-password" maxLength={128} {...form.register("senha")} />
              {form.formState.errors.senha && (
                <p className="text-xs text-destructive">{form.formState.errors.senha.message}</p>
              )}
            </div>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? "Entrando..." : "Entrar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
